// 📄 components/SRSection.tsx
// Pestaña "SR" de Configuraciones Generales (Commerce > Integración):
// service requests creados durante la fase de ejecución del proyecto,
// vinculados a un sistema (Ariba/ERP/CIG/Cloud Connector) y opcionalmente
// a un canal de integración específico. Reutiliza el mismo modelo/servicio
// que "Incidentes" (ver incidenteService.ts) — un SR ES un Incidente con
// fase="Proyecto", solucionRelacionada="commerce" y los campos extra
// categoriaSR/sistema/canal, filtrado por esos campos en esta vista.
import React, { useEffect, useState } from 'react';
import { Plus, RefreshCw, AlertCircle } from 'lucide-react';
import { useProject } from '../../../../../context/ProjectContext.tsx';
import { getIncidentes, Incidente, IncidenteCategoriaSR, IncidentePlataforma, CATEGORIAS_SR, PLATAFORMAS, CANALES_INTEGRACION } from '../../../../../services/incidenteService.ts';
import IncidenteCard from '../../../../Proyectos/components/IncidenteCard.tsx';
import IncidenteFormModal from '../../../../Proyectos/components/IncidenteFormModal.tsx';
import IncidenteDetailModal from '../../../../Proyectos/components/IncidenteDetailModal.tsx';

export default function SRSection() {
  const { project, projectId, esCliente } = useProject();
  const [srs, setSrs] = useState<Incidente[]>([]);
  const [loading, setLoading] = useState(true);
  const [filtroCategoria, setFiltroCategoria] = useState<IncidenteCategoriaSR | 'todas'>('todas');
  const [filtroSistema, setFiltroSistema] = useState<IncidentePlataforma | 'todos'>('todos');
  const [filtroCanal, setFiltroCanal] = useState('todos');
  const [formOpen, setFormOpen] = useState(false);
  const [selected, setSelected] = useState<Incidente | null>(null);

  const miembros = project?.miembros || [];
  const solucionesActivas = project?.solucionesActivas || ['commerce'];

  const loadSRs = async () => {
    setLoading(true);
    try {
      const data = await getIncidentes(projectId);
      // Un SR es un incidente creado desde esta pestaña: siempre trae
      // categoriaSR. Los incidentes "normales" (reportados desde la
      // sección general de Incidentes) no lo tienen y no aparecen acá.
      setSrs(data.filter(i => !!i.categoriaSR));
      setSelected(prev => (prev ? data.find(i => i.id === prev.id) || null : null));
    } catch (err) {
      console.error('Error cargando SRs:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (projectId) loadSRs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId]);

  const filtrados = srs.filter(sr => {
    if (filtroCategoria !== 'todas' && sr.categoriaSR !== filtroCategoria) return false;
    if (filtroSistema !== 'todos' && sr.sistema !== filtroSistema) return false;
    if (filtroCanal !== 'todos' && sr.canal !== filtroCanal) return false;
    return true;
  });

  const handleSaved = () => {
    setFormOpen(false);
    loadSRs();
  };

  return (
    <div className="space-y-6 animate-fadeIn">
      <div>
        <h3 className="text-lg font-medium text-white">Service Requests (SR)</h3>
        <p className="text-sm text-gray-400 mt-1">
          Da seguimiento a issues, solicitudes de habilitación o cambios de configuración levantados durante la ejecución del proyecto, en Test o en PRD.
        </p>
      </div>

      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3 flex-wrap">
          <select
            value={filtroCategoria}
            onChange={(e) => setFiltroCategoria(e.target.value as IncidenteCategoriaSR | 'todas')}
            className="bg-zinc-800 border border-white/10 rounded-lg px-3 py-2 text-xs text-white focus:outline-none"
          >
            <option value="todas">Todas las categorías</option>
            {CATEGORIAS_SR.map(c => <option key={c} value={c}>{c}</option>)}
          </select>

          <select
            value={filtroSistema}
            onChange={(e) => setFiltroSistema(e.target.value as IncidentePlataforma | 'todos')}
            className="bg-zinc-800 border border-white/10 rounded-lg px-3 py-2 text-xs text-white focus:outline-none"
          >
            <option value="todos">Todos los sistemas</option>
            {PLATAFORMAS.map(p => <option key={p} value={p}>{p}</option>)}
          </select>

          <select
            value={filtroCanal}
            onChange={(e) => setFiltroCanal(e.target.value)}
            className="bg-zinc-800 border border-white/10 rounded-lg px-3 py-2 text-xs text-white focus:outline-none"
          >
            <option value="todos">Todos los canales</option>
            {CANALES_INTEGRACION.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
          </select>
        </div>

        {!esCliente && (
          <button
            onClick={() => setFormOpen(true)}
            className="flex items-center gap-2 bg-blue-600 hover:bg-blue-500 px-4 py-2 rounded-xl text-sm font-semibold text-[#fff] transition-all"
          >
            <Plus className="w-4 h-4" /> Nuevo SR
          </button>
        )}
      </div>

      {loading ? (
        <div className="p-10 text-center text-gray-400 flex flex-col items-center gap-3">
          <RefreshCw className="w-6 h-6 animate-spin text-blue-500" />
          <p className="text-sm">Cargando SRs...</p>
        </div>
      ) : filtrados.length === 0 ? (
        <div className="p-10 text-center text-gray-500 bg-white/5 border border-white/10 rounded-2xl">
          <AlertCircle className="w-8 h-8 mx-auto mb-2 text-gray-600" />
          <p className="text-sm">
            {srs.length === 0 ? 'Todavía no hay SRs creados en este proyecto.' : 'Ningún SR coincide con el filtro.'}
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {filtrados.map((sr) => (
            <IncidenteCard key={sr.id} incidente={sr} miembros={miembros} onClick={() => setSelected(sr)} />
          ))}
        </div>
      )}

      {selected && (
        <IncidenteDetailModal
          projectId={projectId}
          incidente={selected}
          miembros={miembros}
          solucionesActivas={solucionesActivas}
          onClose={() => setSelected(null)}
          onUpdated={loadSRs}
        />
      )}

      {formOpen && (
        <IncidenteFormModal
          projectId={projectId}
          solucionesActivas={solucionesActivas}
          miembros={miembros}
          srMode
          onClose={() => setFormOpen(false)}
          onSaved={handleSaved}
        />
      )}
    </div>
  );
}
